import { useEffect } from "react";

const BASE =
  process.env.REACT_APP_SITE_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "https://autobasiccare.com";

const MARK = "data-abc-head";

function cleanManaged() {
  document.querySelectorAll(`[${MARK}]`).forEach((n) => n.remove());
}

function upsertMeta({ name, property, content }) {
  const sel = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  const existing = document.head.querySelector(sel);
  const el = existing || document.createElement("meta");
  if (name) el.setAttribute("name", name);
  if (property) el.setAttribute("property", property);
  el.setAttribute("content", content);
  el.setAttribute(MARK, "1");
  if (!existing) document.head.appendChild(el);
}

function upsertLink({ rel, href }) {
  const existing = document.head.querySelector(`link[rel="${rel}"]`);
  const el = existing || document.createElement("link");
  el.setAttribute("rel", rel);
  el.setAttribute("href", href);
  el.setAttribute(MARK, "1");
  if (!existing) document.head.appendChild(el);
}

function appendScript(json, idx) {
  const el = document.createElement("script");
  el.setAttribute("type", "application/ld+json");
  el.setAttribute(MARK, `ld-${idx}`);
  el.textContent = JSON.stringify(json);
  document.head.appendChild(el);
}

/**
 * Per-route head manager.
 *   const { title, description, path, image, jsonLd, noindex } = props
 */
export default function useHead({
  title,
  description,
  path = "/",
  image = `${BASE}/og-cover.jpg`,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  useEffect(() => {
    const fullTitle = title && title.includes("Auto Basic Care")
      ? title
      : `${title} · Auto Basic Care`;
    const url = `${BASE}${path}`;

    // Remove tags previously injected for another route
    cleanManaged();

    document.title = fullTitle;

    if (description) upsertMeta({ name: "description", content: description });
    upsertLink({ rel: "canonical", href: url });

    if (noindex) upsertMeta({ name: "robots", content: "noindex,nofollow" });

    upsertMeta({ property: "og:type", content: type });
    upsertMeta({ property: "og:site_name", content: "Auto Basic Care" });
    upsertMeta({ property: "og:title", content: fullTitle });
    if (description) upsertMeta({ property: "og:description", content: description });
    upsertMeta({ property: "og:url", content: url });
    upsertMeta({ property: "og:image", content: image });
    upsertMeta({ property: "og:locale", content: "en_US" });

    upsertMeta({ name: "twitter:card", content: "summary_large_image" });
    upsertMeta({ name: "twitter:title", content: fullTitle });
    if (description) upsertMeta({ name: "twitter:description", content: description });
    upsertMeta({ name: "twitter:image", content: image });

    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((ld, i) => appendScript(ld, i));
    }

    return () => {
      cleanManaged();
    };
  }, [title, description, path, image, type, jsonLd, noindex]);
}

/* ---------- Reusable JSON-LD builders ---------- */

export const localBusinessLd = {
  "@context": "https://schema.org",
  "@type": "AutoRepair",
  name: "Auto Basic Care",
  image: `${BASE}/og-cover.jpg`,
  url: BASE,
  telephone: "+1-205-405-1238",
  priceRange: "$$",
  description:
    "Dependable automotive care in Birmingham, AL — diagnostics, brakes, oil changes, tires, AC, suspension, and fleet maintenance for domestic, Asian, and European vehicles.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "1700 31st Street SW",
    addressLocality: "Birmingham",
    addressRegion: "AL",
    postalCode: "35221",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 33.484013,
    longitude: -86.855539,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "08:00",
      closes: "16:30",
    },
  ],
  areaServed: [
    { "@type": "City", name: "Birmingham" },
    { "@type": "AdministrativeArea", name: "Jefferson County" },
    { "@type": "State", name: "Alabama" },
  ],
  hasMap:
    "https://www.google.com/maps?q=1700+31st+Street+SW,+Birmingham,+AL+35221",
  sameAs: [
    "https://www.facebook.com/autobasiccare",
    "https://www.instagram.com/autobasiccare",
    "https://www.tiktok.com/@autobasiccare",
  ],
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Auto Basic Care",
  url: BASE,
  potentialAction: {
    "@type": "SearchAction",
    target: `${BASE}/faq?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${BASE}${it.path}`,
  })),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q || f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a || f.answer,
    },
  })),
});

export const offerCatalogLd = (offers) => ({
  "@context": "https://schema.org",
  "@type": "OfferCatalog",
  name: "Auto Basic Care — Current Specials",
  itemListElement: offers.map((o) => ({
    "@type": "Offer",
    name: o.title,
    price: (o.price || "").replace(/[^0-9.]/g, "") || undefined,
    priceCurrency: "USD",
    description: o.description,
    url: `${BASE}/specials`,
  })),
});
